import React, { CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { HoverCard3D } from './HoverCard3D';
import bookImage from '../../assets/book.png';
import arrowImage from '../../assets/arrow-right.png';
import arrowLeftImage from '../../assets/arrow-left.png';
import drawerPreview03 from '../../assets/03.jpg';
import drawerPreview04 from '../../assets/04.jpeg';
import drawerPreview05 from '../../assets/05.jpeg';
import drawerPreview06 from '../../assets/06.jpg';
import starImage from '../../assets/star.png';
import hecoMaximButterfly from '../../assets/heco-maxim-butterfly.png';
import hecoMaximInnerPeace from '../../assets/heco-maxim-inner-peace.png';
import hecoMaximFalcon from '../../assets/heco-maxim-falcon.png';
import aucklandCourseHistory from '../../assets/auckland-course-history.png';
import './BookSection.css';

gsap.registerPlugin(ScrollTrigger);

const DESKTOP_MIN_WIDTH = 810;
const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;
const INTRO_IMAGE_REMAINING_TOLERANCE_PX = 1;
const BOOK_IMAGE_SCROLL_START = 'top 88%';
const BOOK_IMAGE_SCROLL_END = 'center 42%';
const QUALIFICATION_THEME_START = 'top 60%';
const QUALIFICATION_THEME_END = 'bottom 20%';
const getIsDesktop = () =>
  typeof window !== 'undefined' && window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

export const setQualificationPageTheme = (isActive: boolean) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (isActive) {
    document.body.dataset.qualificationTheme = 'dark';
    return;
  }

  delete document.body.dataset.qualificationTheme;
};

const hashString = (value: string) =>
  Array.from(value).reduce((accumulator, char) => accumulator * 31 + char.charCodeAt(0), 17);

const seededBetween = (seed: number, salt: number, min: number, max: number) => {
  const normalized = Math.sin(seed * 12.9898 + salt * 78.233) * 43758.5453;
  const fraction = normalized - Math.floor(normalized);
  return min + fraction * (max - min);
};

const buildJourneyItemMotionStyle = (seedKey: string, index: number): React.CSSProperties => {
  const seed = hashString(`${seedKey}-${index}`);

  return {
    ['--journey-item-index' as string]: `${index + 1}`,
    ['--journey-item-x' as string]: seededBetween(seed, 1, -1.8, 1.8).toFixed(3),
    ['--journey-item-y' as string]: seededBetween(seed, 2, -2.0, 2.2).toFixed(3),
    ['--journey-item-z' as string]: seededBetween(seed, 3, -1.3, -0.2).toFixed(3),
    ['--journey-item-rx' as string]: seededBetween(seed, 4, -5.5, 5.5).toFixed(3),
    ['--journey-item-ry' as string]: seededBetween(seed, 5, -7, 7).toFixed(3),
    ['--journey-item-rz' as string]: seededBetween(seed, 6, -4.5, 4.5).toFixed(3),
    ['--journey-item-blur' as string]: seededBetween(seed, 7, 0.8, 3.5).toFixed(3),
  };
};

type IntroImageFollowOptions = {
  imageHeight?: number;
  visibleImageOffset?: number;
};

// When part of the intro photo still remains, move that slice into the sticky
// handoff stage so qualification can pause on the photo briefly, then cover it
// as one full panel instead of using a separate normal-flow photo segment.
export const shouldRenderRemainingIntroImage = ({
  imageHeight = 0,
  visibleImageOffset = 0,
}: IntroImageFollowOptions): boolean => {
  if (imageHeight <= 0) {
    return false;
  }

  const remainingIntroImageHeight = Math.max(0, imageHeight - visibleImageOffset);
  return remainingIntroImageHeight > INTRO_IMAGE_REMAINING_TOLERANCE_PX;
};

type EducationCourse = {
  code: string;
  title: string;
  grade: string;
  points: number;
};

type EducationCourseGroup = {
  term: string;
  courses: EducationCourse[];
};

type EducationItem = {
  id: string;
  school: string;
  degree: string;
  years: string;
  grade?: string;
  overview?: string;
  courseGroups?: EducationCourseGroup[];
  transcriptImage?: string;
  transcriptAlt?: string;
};

type ExperienceItem = {
  id: string;
  company: string;
  group: string;
  role: string;
  years: string;
  accent: string;
  details: string[];
  clientsServed: string;
};

type CertificateItem = {
  id: string;
  issuer: string;
  title: string;
  accent: string;
  code: string;
  details: string[];
  mediaImage: string;
  mediaAlt: string;
  mediaAspectRatio: number;
};

type ActiveDrawerItem =
  | { kind: 'education'; item: EducationItem }
  | { kind: 'certificate'; item: CertificateItem }
  | { kind: 'experience'; item: ExperienceItem };

type BookSectionProps = {
  imageHeight?: number;
  visibleImageOffset?: number;
};

type DesktopQualificationSectionProps = {
  mode: 'cover' | 'follow' | 'full';
};

const educationItems: EducationItem[] = [
  {
    id: 'university-of-auckland',
    school: 'University of Auckland',
    degree: 'Master of Computer Science, Information Technology',
    years: '2024 - 2026',
    grade: 'Grade: 7.1/9 (A)',
    overview:
      'My study has combined software development, cloud architecture, cybersecurity, data mining and applied AI with digital health and professional practice. It has strengthened the way I connect technical implementation with human needs, evaluation and responsible decision-making in real-world settings.',
    courseGroups: [
      {
        term: '2025 Semester Two',
        courses: [
          {
            code: 'DIGIHLTH 704',
            title: 'Artificial Intelligence in Healthcare',
            grade: 'A-',
            points: 15,
          },
          {
            code: 'GLMI 706',
            title: 'Working in an Age of Uncertainty',
            grade: 'A+',
            points: 15,
          },
          {
            code: 'INFOSYS 722',
            title: 'Data Mining and Big Data',
            grade: 'A',
            points: 15,
          },
          {
            code: 'INFOSYS 735',
            title: 'Cloud Computing Architecture',
            grade: 'A-',
            points: 15,
          },
        ],
      },
      {
        term: '2025 Semester One',
        courses: [
          {
            code: 'COMPSCI 702',
            title: 'Security for Smart-devices',
            grade: 'A+',
            points: 15,
          },
          {
            code: 'COMPSCI 732',
            title: 'Software Tools and Techniques',
            grade: 'A+',
            points: 15,
          },
          {
            code: 'COMPSCI 747',
            title: 'Computing Education',
            grade: 'B+',
            points: 15,
          },
          {
            code: 'DIGIHLTH 705',
            title: 'Digital Health Design and Evaluation',
            grade: 'A',
            points: 15,
          },
        ],
      },
      {
        term: '2024 Late Year Term',
        courses: [
          {
            code: 'COMPSCI 718',
            title: 'Programming for Industry',
            grade: 'B',
            points: 30,
          },
          {
            code: 'COMPSCI 719',
            title: 'Programming with Web Technologies',
            grade: 'B+',
            points: 30,
          },
        ],
      },
    ],
    transcriptImage: aucklandCourseHistory,
    transcriptAlt:
      'University of Auckland course history showing course codes, descriptions, terms, grades, points and completion status',
  },
  {
    id: 'uestc',
    school: 'University of Electronic Science and Technology of China, Zhongshan Institude',
    degree: 'Bachelor of Art, Journalism',
    years: '2015 - 2019',
  },
];

const experienceItems: ExperienceItem[] = [
  {
    id: 'mindshare-wpp',
    company: 'Mindshare',
    group: 'WPP',
    role: 'Creative Manager',
    years: '2022-2023',
    accent: 'rgba(177, 191, 222, 0.62)',
    details: [
      'Worked on-site with clients, leading the team in day-to-day collaboration, communication management, task coordination, and creative direction.',
      'Monitored marketing trends across mainstream and emerging automotive brands, regularly producing market insight reports, competitive analysis, and strategic recommendations.',
      'Participated in media resource integration, maintained key media partnerships, and supported the execution of multiple innovative media projects.',
    ],
    clientsServed: 'Clients served: Geely Auto, Yadea E-bikes, Ant Financial.',
  },
  {
    id: 'gtb-wpp',
    company: 'GTB',
    group: 'WPP',
    role: 'Strategy Planner',
    years: '2021-2022',
    accent: 'rgba(221, 191, 166, 0.62)',
    details: [
      'Participated in brand and communication strategy planning for Ford, covering new vehicle launches, brand campaigns, and social campaigns, with both strategic and creative contributions.',
      'Contributed to regional marketing planning for JMC Ford by developing localized retail marketing strategies based on regional culture and audience insights.',
      'Improved team and vendor execution efficiency by developing practical frameworks, including reporting templates, guideline manuals, and execution handbooks.',
    ],
    clientsServed: 'Clients served: Ford, JMC Ford.',
  },
  {
    id: 'qm-internet-technology',
    company: 'QM Internet Technology',
    group: '',
    role: 'Social Planner',
    years: '2020-2021',
    accent: 'rgba(164, 205, 193, 0.62)',
    details: [
      'Participated in developing campaign creative strategies and communication plans through integrated marketing approaches, including social creative and media placement.',
      'Regularly contributed to social creative pitches to support new business development; led a major creative proposal that helped the company win Bilibili’s annual creative supplier account.',
    ],
    clientsServed: 'Clients served: Alibaba, Tencent, JD.com, Anta.',
  },
];

const certificateItems: CertificateItem[] = [
  {
    id: 'aws-certified-cloud-practitioner',
    issuer: 'AWS',
    title: 'Certified Cloud Practitioner',
    accent: 'rgba(245, 158, 66, 0.62)',
    code: '05',
    details: [
      'Foundational cloud certification covering AWS core services, architecture basics, pricing, billing, and support.',
      'Validates practical understanding of shared responsibility, security essentials, and common cloud deployment concepts.',
    ],
    mediaImage: drawerPreview06,
    mediaAlt: 'AWS Certified Cloud Practitioner pass certificate',
    mediaAspectRatio: 528 / 682,
  },
  {
    id: 'tencent-creative-marketing-specialist',
    issuer: 'Tencent',
    title: 'Creative Marketing Specialist',
    accent: 'rgba(232, 180, 138, 0.65)',
    code: '01',
    details: [
      'Focused on Tencent Marketing Solution creative planning, campaign ideation, and branded communication execution.',
      'Used to demonstrate platform-side knowledge for creative strategy and integrated marketing collaboration.',
    ],
    mediaImage: drawerPreview03,
    mediaAlt: 'Tencent marketing solution certified creative marketing specialist certificate',
    mediaAspectRatio: 1502 / 1062,
  },
  {
    id: 'bytedance-brand-advertising-marketing-engineer',
    issuer: 'ByteDance',
    title: 'Brand Advertising Marketing Engineer',
    accent: 'rgba(187, 160, 227, 0.62)',
    code: '02',
    details: [
      'Covers Ocean Engine brand advertising products, campaign setup logic, and full-funnel marketing understanding.',
      'Supports execution work that connects audience strategy, ad products, and branded delivery planning.',
    ],
    mediaImage: drawerPreview04,
    mediaAlt: 'Ocean Engine digital marketing competence certificate for brand advertising marketing engineer',
    mediaAspectRatio: 800 / 570,
  },
  {
    id: 'bytedance-marketing-analysis-engineer',
    issuer: 'ByteDance',
    title: 'Marketing Analysis Engineer',
    accent: 'rgba(124, 179, 178, 0.62)',
    code: '03',
    details: [
      'Focuses on Ocean Engine marketing analytics, performance interpretation, and campaign optimization thinking.',
      'Used to validate analytical fluency across reporting, attribution, and data-informed marketing decisions.',
    ],
    mediaImage: drawerPreview05,
    mediaAlt: 'Ocean Engine digital marketing competence certificate for marketing analysis engineer',
    mediaAspectRatio: 800 / 570,
  },
];

const renderJourneyStar = (delay: number) => (
  <span
    className="journey-item-star"
    aria-hidden="true"
    style={{
      '--star-delay': `${delay}s`,
      '--star-image': `url(${starImage})`,
    } as CSSProperties}
  />
);

const formatExperienceLine = (item: ExperienceItem) =>
  [item.company, item.group, item.role, item.years].filter(Boolean).join(', ');

const setBookImageMotionProgress = (node: HTMLElement, progress: number) => {
  const nextProgress = gsap.utils.clamp(0, 1, progress);

  node.style.setProperty('--book-scroll-progress', nextProgress.toFixed(4));
  node.style.setProperty('--book-scroll-factor', (1 - nextProgress).toFixed(4));
};

const useBookImageScrollMotion = (
  motionRef: React.RefObject<HTMLElement>,
  enabled = true
) => {
  useEffect(() => {
    if (!enabled || !motionRef.current) {
      return undefined;
    }

    const motionNode = motionRef.current;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    setBookImageMotionProgress(motionNode, prefersReducedMotion ? 1 : 0);

    if (prefersReducedMotion) {
      return undefined;
    }

    const trigger = ScrollTrigger.create({
      trigger: motionNode,
      start: BOOK_IMAGE_SCROLL_START,
      end: BOOK_IMAGE_SCROLL_END,
      scrub: 1,
      invalidateOnRefresh: true,
      onUpdate: (instance) => {
        setBookImageMotionProgress(motionNode, instance.progress);
      },
      onRefresh: (instance) => {
        setBookImageMotionProgress(motionNode, instance.progress);
      },
    });

    return () => {
      trigger.kill();
    };
  }, [enabled, motionRef]);
};

type JourneyLayoutProps = {
  className?: string;
  layoutRef?: React.Ref<HTMLDivElement>;
  educationRef?: React.Ref<HTMLElement>;
  workRef?: React.Ref<HTMLElement>;
  certificationsRef?: React.Ref<HTMLElement>;
  educationMotionStyles?: React.CSSProperties[];
  experienceMotionStyles?: React.CSSProperties[];
  certificationMotionStyles?: React.CSSProperties[];
  activeDrawerItem?: ActiveDrawerItem | null;
  onExperienceSelect?: (item: ExperienceItem) => void;
  onCertificateSelect?: (item: CertificateItem) => void;
};

const JourneyLayout: React.FC<JourneyLayoutProps> = ({
  className = '',
  layoutRef,
  educationRef,
  workRef,
  certificationsRef,
  educationMotionStyles,
  experienceMotionStyles,
  certificationMotionStyles,
  activeDrawerItem = null,
  onExperienceSelect,
  onCertificateSelect,
}) => {
  const isInteractive = Boolean(onExperienceSelect && onCertificateSelect);
  const layoutClassName = ['journey-layout', className].filter(Boolean).join(' ');

  return (
    <div ref={layoutRef} className={layoutClassName}>
      <section ref={educationRef} className="journey-block journey-block--education">
        <h3 className="section-title"><span className="section-title-text">education</span></h3>
        <div className="education-list">
          {educationItems.map((item, index) => (
            <div
              className="education-entry"
              key={item.id}
              style={educationMotionStyles?.[index]}
            >
              {renderJourneyStar(index * -0.28)}
              <div className="education-entry-copy">
                <p className="education-entry-school">{item.school}</p>
                <p className="education-entry-degree">{`${item.degree}, ${item.years}`}</p>
                {item.grade ? <p className="education-entry-grade">{item.grade}</p> : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="journey-arrow journey-arrow--to-work" aria-hidden="true">
        <img src={arrowImage} alt="" className="journey-arrow-image" />
      </div>

      <section ref={workRef} className="journey-block journey-block--work">
        <h3 className="section-title"><span className="section-title-text">work experience</span></h3>
        <div className="experience-list">
          {experienceItems.map((item, index) => {
            const isActive =
              activeDrawerItem?.kind === 'experience' && activeDrawerItem.item.id === item.id;

            return (
              <div
                className="experience-line"
                key={item.id}
                style={experienceMotionStyles?.[index]}
              >
                {isInteractive ? (
                  <button
                    type="button"
                    className={`journey-trigger${isActive ? ' is-active' : ''}`}
                    onClick={() => onExperienceSelect?.(item)}
                  >
                    {renderJourneyStar(index * -0.28)}
                    <span className="journey-item-text journey-trigger-text">
                      {formatExperienceLine(item)}
                    </span>
                  </button>
                ) : (
                  <div className="journey-trigger journey-trigger--static">
                    {renderJourneyStar(index * -0.28)}
                    <span className="journey-item-text">{formatExperienceLine(item)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="journey-arrow journey-arrow--to-certifications" aria-hidden="true">
        <img
          src={arrowLeftImage}
          alt=""
          className="journey-arrow-image journey-arrow-image--left"
        />
      </div>

      <section ref={certificationsRef} className="journey-block journey-block--certifications">
        <h3 className="section-title"><span className="section-title-text">certifications</span></h3>
        <div className="certificate-list">
          {certificateItems.map((item, index) => {
            const isActive =
              activeDrawerItem?.kind === 'certificate' && activeDrawerItem.item.id === item.id;

            return (
              <div
                className="certificate-line"
                key={item.id}
                style={certificationMotionStyles?.[index]}
              >
                {isInteractive ? (
                  <button
                    type="button"
                    className={`journey-trigger${isActive ? ' is-active' : ''}`}
                    onClick={() => onCertificateSelect?.(item)}
                  >
                    {renderJourneyStar(index * -0.28)}
                    <span className="journey-item-text journey-trigger-text">
                      {item.issuer}, {item.title}
                    </span>
                  </button>
                ) : (
                  <div className="journey-trigger journey-trigger--static">
                    {renderJourneyStar(index * -0.28)}
                    <span className="journey-item-text">
                      {item.issuer}, {item.title}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

type QualificationDrawerProps = {
  activeDrawerItem: ActiveDrawerItem | null;
  onClose: () => void;
};

const QualificationDrawer: React.FC<QualificationDrawerProps> = ({
  activeDrawerItem,
  onClose,
}) => {
  const drawerDialogRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!activeDrawerItem || !drawerDialogRef.current) {
      return undefined;
    }

    const dialogNode = drawerDialogRef.current;

    const resetScrollPosition = () => {
      dialogNode.scrollTop = 0;
      dialogNode.scrollLeft = 0;

      if (typeof dialogNode.scrollTo === 'function') {
        dialogNode.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    resetScrollPosition();
    const frameId = window.requestAnimationFrame(resetScrollPosition);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeDrawerItem]);

  useEffect(() => {
    if (!activeDrawerItem) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    drawerDialogRef.current?.focus();

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeDrawerItem, onClose]);

  const activeCertificate =
    activeDrawerItem?.kind === 'certificate' ? activeDrawerItem.item : null;
  const activeExperience =
    activeDrawerItem?.kind === 'experience' ? activeDrawerItem.item : null;
  const activeEducation =
    activeDrawerItem?.kind === 'education' ? activeDrawerItem.item : null;
  const activeItem = activeEducation ?? activeExperience ?? activeCertificate;
  const activeDrawerId =
    activeDrawerItem && activeItem ? `${activeDrawerItem.kind}-${activeItem.id}` : '';
  const activeDrawerAccent =
    activeExperience?.accent ?? activeCertificate?.accent ?? 'rgba(200, 192, 176, 0.6)';

  const drawerNode = (
    <>
      <button
        type="button"
        className={`role-drawer-overlay${activeDrawerItem ? ' open' : ''}`}
        aria-label="Close details backdrop"
        onClick={onClose}
      />
      <aside
        className={`role-drawer${activeDrawerItem ? ' open' : ''}`}
        aria-hidden={!activeDrawerItem}
      >
        <button
          type="button"
          className="role-drawer-close"
          aria-label="Close details"
          onClick={onClose}
        >
          <X size={20} weight="regular" aria-hidden="true" />
        </button>
        {activeEducation ? (
          <div
            key={activeDrawerId}
            ref={drawerDialogRef}
            className="role-drawer-inner role-drawer-inner--education"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${activeDrawerId}-title`}
            aria-describedby={`${activeDrawerId}-description`}
            tabIndex={-1}
          >
            <div className="role-drawer-copy role-drawer-copy--education">
              <h3
                className="role-drawer-title role-drawer-title--experience"
                id={`${activeDrawerId}-title`}
              >
                {activeEducation.school}
              </h3>
              <div className="role-drawer-body" id={`${activeDrawerId}-description`}>
                <div className="role-drawer-points">
                  {[activeEducation.degree, `Study period: ${activeEducation.years}`]
                    .concat(activeEducation.grade ? [activeEducation.grade] : [])
                    .map((detail, index) => (
                      <div className="role-drawer-point" key={`${activeEducation.id}-detail-${index}`}>
                        {renderJourneyStar(index * -0.28)}
                        <p className="role-drawer-point-text">{detail}</p>
                      </div>
                    ))}
                </div>
                {activeEducation.overview ? (
                  <section
                    className="role-drawer-education-section"
                    aria-labelledby={`${activeDrawerId}-focus-title`}
                  >
                    <h4
                      className="role-drawer-section-label"
                      id={`${activeDrawerId}-focus-title`}
                    >
                      What I studied
                    </h4>
                    <p className="role-drawer-education-overview">
                      {activeEducation.overview}
                    </p>
                  </section>
                ) : null}
                {activeEducation.courseGroups?.length ? (
                  <section
                    className="role-drawer-education-section"
                    aria-labelledby={`${activeDrawerId}-coursework-title`}
                  >
                    <div className="role-drawer-section-heading">
                      <h4
                        className="role-drawer-section-label"
                        id={`${activeDrawerId}-coursework-title`}
                      >
                        Coursework
                      </h4>
                      <span>180 points</span>
                    </div>
                    <div className="role-drawer-course-groups">
                      {activeEducation.courseGroups.map((group) => (
                        <section className="role-drawer-course-group" key={group.term}>
                          <h5>{group.term}</h5>
                          <ul className="role-drawer-course-list">
                            {group.courses.map((course) => (
                              <li
                                className="role-drawer-course-row"
                                key={`${group.term}-${course.code}`}
                              >
                                <span className="role-drawer-course-code">{course.code}</span>
                                <span className="role-drawer-course-title">{course.title}</span>
                                <span className="role-drawer-course-points">
                                  {course.points} pts
                                </span>
                                <strong className="role-drawer-course-grade">
                                  {course.grade}
                                </strong>
                              </li>
                            ))}
                          </ul>
                        </section>
                      ))}
                    </div>
                  </section>
                ) : null}
                {activeEducation.transcriptImage ? (
                  <section
                    className="role-drawer-education-section role-drawer-transcript"
                    aria-labelledby={`${activeDrawerId}-transcript-title`}
                  >
                    <div className="role-drawer-section-heading">
                      <span
                        className="role-drawer-section-label"
                        id={`${activeDrawerId}-transcript-title`}
                      >
                        Course history
                      </span>
                    </div>
                    <div className="role-drawer-transcript-frame">
                      <img
                        src={activeEducation.transcriptImage}
                        alt={activeEducation.transcriptAlt ?? 'University course history'}
                        loading="lazy"
                      />
                    </div>
                  </section>
                ) : null}
              </div>
            </div>
          </div>
        ) : activeCertificate ? (
          <div
            key={activeDrawerId}
            ref={drawerDialogRef}
            className="role-drawer-inner role-drawer-inner--certificate"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${activeDrawerId}-title`}
            aria-describedby={`${activeDrawerId}-description`}
            tabIndex={-1}
          >
            <div className="role-drawer-copy role-drawer-copy--certificate">
              <div className="role-drawer-media role-drawer-media--certificate" aria-hidden="true">
                <div
                  className="role-drawer-card role-drawer-card--media role-drawer-card--stacked-media"
                  style={{
                    '--drawer-accent': activeDrawerAccent,
                    '--drawer-media-ratio': `${activeCertificate.mediaAspectRatio}`,
                  } as CSSProperties}
                >
                  <div className="role-drawer-image-shell">
                    <img
                      className="role-drawer-image"
                      src={activeCertificate.mediaImage}
                      alt={activeCertificate.mediaAlt}
                    />
                  </div>
                </div>
              </div>
              <h3 className="role-drawer-title" id={`${activeDrawerId}-title`}>
                {activeCertificate.issuer}, {activeCertificate.title}
              </h3>
              <div className="role-drawer-body" id={`${activeDrawerId}-description`}>
                <div className="role-drawer-points">
                  {activeCertificate.details.map((detail, index) => (
                    <div className="role-drawer-point" key={`${activeCertificate.id}-detail-${index}`}>
                      {renderJourneyStar(index * -0.28)}
                      <p className="role-drawer-point-text">{detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : activeExperience ? (
          <div
            key={activeDrawerId}
            ref={drawerDialogRef}
            className="role-drawer-inner role-drawer-inner--experience"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${activeDrawerId}-title`}
            aria-describedby={`${activeDrawerId}-description`}
            tabIndex={-1}
          >
            <div className="role-drawer-copy role-drawer-copy--experience">
              <h3
                className="role-drawer-title role-drawer-title--experience"
                id={`${activeDrawerId}-title`}
              >
                {formatExperienceLine(activeExperience)}
              </h3>
              <div className="role-drawer-body" id={`${activeDrawerId}-description`}>
                <div className="role-drawer-points">
                  {activeExperience.details.map((detail, index) => (
                    <div className="role-drawer-point" key={`${activeExperience.id}-detail-${index}`}>
                      {renderJourneyStar(index * -0.28)}
                      <p className="role-drawer-point-text">{detail}</p>
                    </div>
                  ))}
                  <div className="role-drawer-point" key={`${activeExperience.id}-clients`}>
                    {renderJourneyStar(activeExperience.details.length * -0.28)}
                    <p className="role-drawer-point-text role-drawer-point-text--clients">
                      {activeExperience.clientsServed}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </aside>
    </>
  );

  if (typeof document === 'undefined') {
    return drawerNode;
  }

  return createPortal(drawerNode, document.body);
};

const qualificationPanelLabels = [
  'education',
  'work experience',
  'certifications',
] as const;

export const getQualificationRowRevealProgress = (
  panelProgress: number,
  rowIndex: number,
  rowCount: number
) => {
  const clampedProgress = Math.min(1, Math.max(0, panelProgress));
  const safeRowCount = Math.max(1, rowCount);
  const safeRowIndex = Math.min(safeRowCount - 1, Math.max(0, rowIndex));
  const rowStep = 1 / safeRowCount;
  const revealStart = safeRowIndex * rowStep * 0.72;
  const revealDuration = Math.min(0.5, Math.max(0.28, rowStep * 2.1));

  return Math.min(
    1,
    Math.max(0, (clampedProgress - revealStart) / revealDuration)
  );
};

const QualificationMaximsSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const panelRefs = useRef<Array<HTMLElement | null>>([]);
  const bookMotionRef = useRef<HTMLDivElement>(null);
  const [activePanelIndex, setActivePanelIndex] = useState(0);
  const [isSectionInView, setIsSectionInView] = useState(false);
  const [activeDrawerItem, setActiveDrawerItem] = useState<ActiveDrawerItem | null>(null);

  useBookImageScrollMotion(bookMotionRef);

  useLayoutEffect(() => {
    const panels = panelRefs.current.filter(
      (panel): panel is HTMLElement => panel !== null
    );

    if (
      panels.length !== qualificationPanelLabels.length ||
      !sectionRef.current
    ) {
      return undefined;
    }

    const section = sectionRef.current;
    const revealRowsByPanel = panels.map((panel) =>
      Array.from(
        panel.querySelectorAll<HTMLElement>('[data-qualification-reveal-row]')
      )
    );
    const updatePanelRows = (panelIndex: number, panelProgress: number) => {
      const revealRows = revealRowsByPanel[panelIndex];

      revealRows.forEach((row, rowIndex) => {
        row.style.setProperty(
          '--qualification-row-progress',
          getQualificationRowRevealProgress(
            panelProgress,
            rowIndex,
            revealRows.length
          ).toFixed(4)
        );
      });
    };
    const setQualificationTheme = (isActive: boolean) => {
      setIsSectionInView(isActive);
      setQualificationPageTheme(isActive);
    };

    const sectionTrigger = ScrollTrigger.create({
      trigger: section,
      start: QUALIFICATION_THEME_START,
      end: QUALIFICATION_THEME_END,
      invalidateOnRefresh: true,
      onToggle: (instance) => setQualificationTheme(instance.isActive),
      onRefresh: (instance) => setQualificationTheme(instance.isActive),
    });

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      revealRowsByPanel.forEach((_, panelIndex) => {
        updatePanelRows(panelIndex, 1);
      });

      return () => {
        sectionTrigger.kill();
        setQualificationPageTheme(false);
      };
    }

    const triggers = panels.map((panel, index) =>
      ScrollTrigger.create({
        trigger: panel,
        start: 'top 58%',
        end: 'bottom 42%',
        invalidateOnRefresh: true,
        onEnter: () => setActivePanelIndex(index),
        onEnterBack: () => setActivePanelIndex(index),
        onUpdate: (instance) => updatePanelRows(index, instance.progress),
        onRefresh: (instance) => {
          updatePanelRows(index, instance.progress);

          if (instance.isActive) {
            setActivePanelIndex(index);
          }
        },
      })
    );

    return () => {
      sectionTrigger.kill();
      triggers.forEach((trigger) => trigger.kill());
      setQualificationPageTheme(false);
    };
  }, []);

  const scrollToPanel = (index: number) => {
    const panel = panelRefs.current[index];

    if (!panel) {
      return;
    }

    setActivePanelIndex(index);
    panel.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'start',
    });
  };

  return (
    <section
      ref={sectionRef}
      className="qualification-whole qualification-whole--full qualification-maxims"
      data-name-overlap-ignore="true"
      data-bg-color="#282828"
      data-text-color="#FFF"
      data-in-view={isSectionInView ? 'true' : 'false'}
      aria-label="Qualifications"
    >
      <div className="qualification-maxims__grid">
        <div className="qualification-maxims__left">
          <nav className="qualification-maxims__nav" aria-label="Qualification sections">
            {qualificationPanelLabels.map((label, index) => {
              const isActive = index === activePanelIndex;

              return (
                <h3 className="qualification-maxims__nav-heading" key={label}>
                  <button
                    type="button"
                    className={`qualification-maxims__nav-button${
                      isActive ? ' is-active' : ''
                    }`}
                    aria-current={isActive ? 'step' : undefined}
                    aria-controls={`qualification-panel-${index}`}
                    onClick={() => scrollToPanel(index)}
                  >
                    {label}
                  </button>
                </h3>
              );
            })}
          </nav>
        </div>

        <div className="qualification-maxims__panels">
          <section
            ref={(node) => {
              panelRefs.current[0] = node;
            }}
            id="qualification-panel-0"
            className="qualification-maxims__panel qualification-maxims__panel--education"
            data-active={activePanelIndex === 0 ? 'true' : 'false'}
            aria-label="Education details"
          >
            <div className="qualification-maxims__art qualification-maxims__art--butterfly">
              <div ref={bookMotionRef} className="book-image-motion-shell">
                <HoverCard3D className="qualification-maxims__art-hover book-image-hover-layer">
                  <img
                    src={hecoMaximButterfly}
                    alt="Line drawing of a butterfly landing on water"
                    className="qualification-maxims__art-image qualification-maxims__art-image--butterfly"
                  />
                </HoverCard3D>
              </div>
            </div>
            <h3 className="qualification-maxims__panel-title">
              {qualificationPanelLabels[0]}
            </h3>
            <div className="qualification-maxims__education-list">
              {educationItems.map((item) => {
                const isActive =
                  activeDrawerItem?.kind === 'education' &&
                  activeDrawerItem.item.id === item.id;

                return (
                  <article
                    className="qualification-maxims__education-item"
                    data-qualification-reveal-row
                    key={item.id}
                  >
                    <button
                      type="button"
                      className={`qualification-maxims__education-button${
                        isActive ? ' is-active' : ''
                      }`}
                      aria-pressed={isActive}
                      onClick={() => setActiveDrawerItem({ kind: 'education', item })}
                    >
                      <span className="qualification-maxims__education-copy">
                        <span className="qualification-maxims__education-school">
                          {item.school}
                        </span>
                        <span className="qualification-maxims__education-meta">
                          {item.degree}
                        </span>
                        <span className="qualification-maxims__education-meta">
                          {item.years}
                        </span>
                        {item.grade ? (
                          <span className="qualification-maxims__education-meta">
                            {item.grade}
                          </span>
                        ) : null}
                      </span>
                      <span className="qualification-maxims__education-arrow" aria-hidden="true">
                        ↗
                      </span>
                    </button>
                  </article>
                );
              })}
            </div>
          </section>

          <section
            ref={(node) => {
              panelRefs.current[1] = node;
            }}
            id="qualification-panel-1"
            className="qualification-maxims__panel qualification-maxims__panel--work"
            data-active={activePanelIndex === 1 ? 'true' : 'false'}
            aria-label="Work experience details"
          >
            <div className="qualification-maxims__art qualification-maxims__art--inner-peace">
              <img
                src={hecoMaximInnerPeace}
                alt="Line drawing of a person bending over a laptop"
                className="qualification-maxims__art-image qualification-maxims__art-image--inner-peace"
              />
            </div>
            <h3 className="qualification-maxims__panel-title">
              {qualificationPanelLabels[1]}
            </h3>
            <div className="qualification-maxims__item-list">
              {experienceItems.map((item) => {
                const isActive =
                  activeDrawerItem?.kind === 'experience' &&
                  activeDrawerItem.item.id === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`qualification-maxims__detail-button${
                      isActive ? ' is-active' : ''
                    }`}
                    data-qualification-reveal-row
                    aria-pressed={isActive}
                    onClick={() => setActiveDrawerItem({ kind: 'experience', item })}
                  >
                    <span>{formatExperienceLine(item)}</span>
                    <span aria-hidden="true">↗</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section
            ref={(node) => {
              panelRefs.current[2] = node;
            }}
            id="qualification-panel-2"
            className="qualification-maxims__panel qualification-maxims__panel--certifications"
            data-active={activePanelIndex === 2 ? 'true' : 'false'}
            aria-label="Certification details"
          >
            <div className="qualification-maxims__art qualification-maxims__art--falcon">
              <img
                src={hecoMaximFalcon}
                alt="Line drawing of a person holding a falcon"
                className="qualification-maxims__art-image qualification-maxims__art-image--falcon"
              />
            </div>
            <h3 className="qualification-maxims__panel-title">
              {qualificationPanelLabels[2]}
            </h3>
            <div className="qualification-maxims__item-list">
              {certificateItems.map((item) => {
                const isActive =
                  activeDrawerItem?.kind === 'certificate' &&
                  activeDrawerItem.item.id === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`qualification-maxims__detail-button${
                      isActive ? ' is-active' : ''
                    }`}
                    data-qualification-reveal-row
                    aria-pressed={isActive}
                    onClick={() => setActiveDrawerItem({ kind: 'certificate', item })}
                  >
                    <span>{item.issuer}, {item.title}</span>
                    <span aria-hidden="true">↗</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      <QualificationDrawer
        activeDrawerItem={activeDrawerItem}
        onClose={() => setActiveDrawerItem(null)}
      />
    </section>
  );
};

const MobileBookSectionContent: React.FC = () => <QualificationMaximsSection />;

const LegacyDesktopQualificationSection: React.FC<DesktopQualificationSectionProps> = ({ mode }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const bookMotionRef = useRef<HTMLDivElement>(null);
  const educationRef = useRef<HTMLElement>(null);
  const workRef = useRef<HTMLElement>(null);
  const certificationsRef = useRef<HTMLElement>(null);
  const [activeDrawerItem, setActiveDrawerItem] = useState<ActiveDrawerItem | null>(null);
  const educationMotionStyles = useMemo(
    () => educationItems.map((item, index) => buildJourneyItemMotionStyle(item.id, index)),
    []
  );
  const experienceMotionStyles = useMemo(
    () => experienceItems.map((item, index) => buildJourneyItemMotionStyle(item.id, index)),
    []
  );
  const certificationMotionStyles = useMemo(
    () => certificateItems.map((item, index) => buildJourneyItemMotionStyle(item.id, index)),
    []
  );
  const hasInteractiveJourney = mode !== 'cover';
  const shouldRenderLead = mode !== 'follow';
  const shouldRenderJourney = mode !== 'cover';

  useBookImageScrollMotion(bookMotionRef, shouldRenderLead);

  useEffect(() => {
    if (mode === 'cover' || !sectionRef.current) return undefined;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const section = sectionRef.current;
    const allLines = Array.from(section.querySelectorAll<HTMLElement>(
      '.section-title-text, .education-entry-copy, .journey-item-text'
    ));
    const lerpFactors = allLines.map((_, i) => Math.max(0.04, 0.12 - i * 0.007));
    const lineY = allLines.map(() => 0);
    let velocityY = 0;
    let lastScrollY = window.scrollY;

    const onTick = () => {
      const scrollY = window.scrollY;
      velocityY += (scrollY - lastScrollY - velocityY) * 0.2;
      lastScrollY = scrollY;
      allLines.forEach((el, i) => {
        lineY[i] += (velocityY * 0.5 - lineY[i]) * lerpFactors[i];
        el.style.transform = `translateY(${lineY[i].toFixed(3)}px)`;
      });
    };

    gsap.ticker.add(onTick);

    return () => {
      gsap.ticker.remove(onTick);
      allLines.forEach(el => { el.style.transform = ''; });
    };
  }, [mode]);

  useEffect(() => {
    if (
      mode === 'cover' ||
      !sectionRef.current ||
      !educationRef.current ||
      !workRef.current ||
      !certificationsRef.current
    ) {
      return undefined;
    }

    const section = sectionRef.current;
    const contentBlocks = [
      educationRef.current,
      workRef.current,
      certificationsRef.current,
    ];
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const triggers: ScrollTrigger[] = [];

    const setBlockProgress = (block: HTMLElement, progress: number) => {
      const nextProgress = gsap.utils.clamp(0, 1, progress);
      const nextFactor = Math.max(1 - nextProgress * 1.7, 0);

      block.style.setProperty('--journey-progress', nextProgress.toFixed(4));
      block.style.setProperty('--journey-factor', nextFactor.toFixed(4));
    };

    // Block animation ranges within the layout progress (staggered assembly)
    const blockRanges: Array<[number, number]> = [
      [0.05, 0.42],  // education
      [0.20, 0.57],  // work experience
      [0.35, 0.72],  // certifications
    ];

    const setLayoutProgress = (progress: number) => {
      const nextProgress = gsap.utils.clamp(0, 1, progress);
      const nextFactor = Math.max(1 - nextProgress * 1.15, 0);

      section.style.setProperty('--journey-layout-progress', nextProgress.toFixed(4));
      section.style.setProperty('--journey-layout-factor', nextFactor.toFixed(4));

      // Drive block scatter animations from the layout scroll progress
      contentBlocks.forEach((block, i) => {
        const [start, end] = blockRanges[i];
        const blockP = gsap.utils.clamp(0, 1, (nextProgress - start) / (end - start));
        setBlockProgress(block, blockP);
      });
    };

    section.style.setProperty('--journey-cx', '0');
    section.style.setProperty('--journey-cy', '0');
    setLayoutProgress(prefersReducedMotion ? 1 : 0);

    if (!prefersReducedMotion) {
      triggers.push(
        ScrollTrigger.create({
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
          invalidateOnRefresh: true,
          onUpdate: (instance) => {
            setLayoutProgress(instance.progress);
          },
          onRefresh: (instance) => {
            setLayoutProgress(instance.progress);
          },
        })
      );
    }

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, [mode]);

  return (
    <section
      ref={sectionRef}
      className={`qualification-whole qualification-whole--${mode}`}
      data-name-overlap-ignore="true"
      aria-hidden={hasInteractiveJourney ? undefined : true}
    >
      <div className="qualification-whole-inner">
        {shouldRenderLead ? (
          <div className="qualification-whole-lead">
            <div ref={bookMotionRef} className="qualification-whole-book book-image-motion-shell">
              <HoverCard3D className="qualification-whole-book-motion book-image-hover-layer">
                <img
                  src={bookImage}
                  alt="Book"
                  className="feature-image feature-image--book qualification-whole-book-image"
                />
              </HoverCard3D>
            </div>
          </div>
        ) : null}

        {shouldRenderJourney ? (
          <JourneyLayout
            className="journey-layout--follow qualification-whole-journey"
            educationRef={educationRef}
            workRef={workRef}
            certificationsRef={certificationsRef}
            educationMotionStyles={educationMotionStyles}
            experienceMotionStyles={experienceMotionStyles}
            certificationMotionStyles={certificationMotionStyles}
            activeDrawerItem={activeDrawerItem}
            onExperienceSelect={(item) => setActiveDrawerItem({ kind: 'experience', item })}
            onCertificateSelect={(item) => setActiveDrawerItem({ kind: 'certificate', item })}
          />
        ) : null}
      </div>

      {hasInteractiveJourney ? (
        <QualificationDrawer
          activeDrawerItem={activeDrawerItem}
          onClose={() => setActiveDrawerItem(null)}
        />
      ) : null}
    </section>
  );
};

const DesktopQualificationSection: React.FC<DesktopQualificationSectionProps> = ({ mode }) =>
  mode === 'full' ? (
    <QualificationMaximsSection />
  ) : (
    <LegacyDesktopQualificationSection mode={mode} />
  );

export const BookSectionFollow: React.FC = () => <DesktopQualificationSection mode="follow" />;
export const BookSectionCover: React.FC = () => <DesktopQualificationSection mode="cover" />;
export const BookSectionWhole: React.FC = () => <DesktopQualificationSection mode="full" />;

export const BookSection: React.FC<BookSectionProps> = ({
  imageHeight = 0,
  visibleImageOffset = 0,
}) => {
  const [isDesktop, setIsDesktop] = useState(getIsDesktop);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  if (!isDesktop) {
    return <MobileBookSectionContent />;
  }

  void imageHeight;
  void visibleImageOffset;

  return <DesktopQualificationSection mode="full" />;
};
